import { useMemo } from 'react';
import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useApp } from '@backstage/core-plugin-api';
import { FeatureFlagged } from '@backstage/core-app-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
// Icons
import SearchIcon from '@material-ui/icons/Search';
import HomeIcon from '@material-ui/icons/Home';
import FolderIcon from '@material-ui/icons/Folder';
import AssessmentIcon from '@material-ui/icons/Assessment';
import {
  getStaticContentSubmenuPermission,
  getStaticContentSubmenuRegistry,
} from '../../plugins/staticContent/staticContentPlugin';
import { useStaticExternalPageGroups } from '../../plugins/staticExternalPages/useStaticExternalPageGroups';
import { ExternalPageSubmenuGroup } from '../../plugins/staticExternalPages/ExternalPageSubmenuGroup';
import {
  getHostedAppPermission,
  hostedAppPath,
  useHostedAppGroups,
} from '../../plugins/hostedApplications/hostedApps';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const externalPageGroups = useStaticExternalPageGroups();
      const hostedAppGroups = useHostedAppGroups();

      // navItems.take()/rest() mutate a collection that's only recreated when
      // the underlying route/extension set changes -- NOT on every React
      // re-render of this component. Async hooks above (e.g.
      // useStaticExternalPageGroups) trigger extra re-renders via setState,
      // and re-running these takes against the same already-mutated
      // collection would silently drop static-content items (already taken)
      // from `.rest()` on the second pass. Doing every take exactly once per
      // `navItems` instance, memoized, avoids that.
      const {
        submenuGroups,
        flatStaticItemsRendered,
        scaffolderNavItem,
        searchRendered,
        mcpChatRendered,
        techdocsRendered,
        scorecardsRendered,
        appVisualizerRendered,
        devtoolsRendered,
        userSettingsRendered,
        restRendered,
      } = useMemo(() => {
        const staticContentItems = navItems
          .rest()
          .filter(item => item.node.spec.plugin?.pluginId === 'static-content');

        const submenuRegistry = getStaticContentSubmenuRegistry();
        const groups = new Map<string, typeof staticContentItems>();
        const flatStaticItems: typeof staticContentItems = [];

        for (const item of staticContentItems) {
          const submenuLabel = submenuRegistry.get(item.node.spec.id);
          if (submenuLabel) {
            if (!groups.has(submenuLabel)) groups.set(submenuLabel, []);
            groups.get(submenuLabel)!.push(item);
          } else {
            flatStaticItems.push(item);
          }
        }

        const nav = navItems.withComponent(item => (
          <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
        ));

        // Always claim static-content items regardless of permission outcome
        // — otherwise a group hidden by RequirePermission below would leak
        // its items into the "Overflow Content" fallback via nav.rest().
        for (const items of groups.values()) {
          for (const item of items) nav.take(item.node.spec.id);
        }

        // WORKAROUND
        nav.take('page:catalog'); // suppressed — custom catalog item used instead
        nav.take('page:announcements'); // suppressed — custom announcements item used instead
        nav.take('page:catalog-graph'); // suppressed — hidden from sidebar
        nav.take('page:static-external-pages/external-page'); // rendered via externalPageGroups below instead
        nav.take('page:hosted-applications/hosted-app'); // rendered via hostedAppGroups below instead
        nav.take('page:notifications'); // suppressed — NotificationsSidebarItem (unread badge) used instead

        return {
          submenuGroups: groups,
          flatStaticItemsRendered: flatStaticItems.map(item => nav.take(item.node.spec.id)),
          // Retitled "Create" -> "Scaffolders"; take raw (not via `nav`) to override its text
          scaffolderNavItem: navItems.take('page:scaffolder'),
          searchRendered: nav.take('page:search'),
          mcpChatRendered: nav.take('page:mcp-chat'),
          techdocsRendered: nav.take('page:techdocs'),
          scorecardsRendered: nav.take('page:scorecards'),
          appVisualizerRendered: nav.take('page:app-visualizer'),
          devtoolsRendered: nav.take('page:devtools'),
          userSettingsRendered: nav.take('page:user-settings'),
          restRendered: nav.rest({ sortBy: 'title' }),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [navItems]);

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            {searchRendered}
            <FeatureFlagged with="mcp-chat">
              {mcpChatRendered}
            </FeatureFlagged>
          </SidebarGroup>
          <SidebarDivider />
          <SidebarItem icon={HomeIcon} text="Home" to="/announcements">
            <SidebarSubmenu title="Catalog">
              <SidebarSubmenuItem
                title="Domains"
                to="catalog?filters[kind]=domain"
                icon={useApp().getSystemIcon('kind:domain')}
              />
              <SidebarSubmenuItem
                title="Systems"
                to="catalog?filters[kind]=system"
                icon={useApp().getSystemIcon('kind:system')}
              />
              <SidebarSubmenuItem
                title="Components"
                to="catalog?filters[kind]=component"
                icon={useApp().getSystemIcon('kind:component')}
              />
              <SidebarSubmenuItem
                title="APIs"
                to="catalog?filters[kind]=api"
                icon={useApp().getSystemIcon('kind:api')}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Resources"
                to="catalog?filters[kind]=resource"
                icon={useApp().getSystemIcon('kind:resource')}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Groups"
                to="catalog?filters[kind]=group"
                icon={useApp().getSystemIcon('kind:group')}
              />
              <SidebarSubmenuItem
                title="Users"
                to="catalog?filters[kind]=user"
                icon={useApp().getSystemIcon('kind:user')}
              />
            </SidebarSubmenu>
          </SidebarItem>
          {techdocsRendered}
          {scorecardsRendered}
          <SidebarItem
            icon={AssessmentIcon}
            text="E360 Reports"
            to="https://didactic-adventure-8w1z7ke.pages.github.io/"
            target="_blank"
            rel="noopener noreferrer"
          />
          {scaffolderNavItem && (
            <SidebarItem
              icon={() => scaffolderNavItem.icon}
              to={scaffolderNavItem.href}
              text="Scaffolders"
            />
          )}
          <SidebarDivider />
          {/* Static Content */}
          <SidebarScrollWrapper>
            {[...submenuGroups.entries()].map(([submenuLabel, items]) => (
              <RequirePermission
                key={submenuLabel}
                permission={getStaticContentSubmenuPermission(submenuLabel)}
                errorPage={<></>}
              >
                <SidebarItem icon={FolderIcon} text={submenuLabel} to={items[0].href}>
                  <SidebarSubmenu title={submenuLabel}>
                    {items.map((item: any) => (
                      <SidebarSubmenuItem
                        key={item.node.spec.id}
                        title={item.title}
                        to={item.href}
                        icon={() => item.icon}
                      />
                    ))}
                  </SidebarSubmenu>
                </SidebarItem>
              </RequirePermission>
            ))}
            {flatStaticItemsRendered}
            {[...externalPageGroups.entries()].map(([submenuLabel, pages]) => (
              <ExternalPageSubmenuGroup
                key={submenuLabel}
                submenuLabel={submenuLabel}
                pages={pages}
              />
            ))}
            {[...hostedAppGroups.entries()].map(([submenuLabel, apps]) => (
              <ExternalPageSubmenuGroup
                key={`hosted-${submenuLabel}`}
                submenuLabel={submenuLabel}
                pages={apps}
                getPermission={getHostedAppPermission}
                getPath={hostedAppPath}
              />
            ))}
          </SidebarScrollWrapper>
          <SidebarDivider />
          {/* Overflow Content */}
          <SidebarScrollWrapper>
            {restRendered}
          </SidebarScrollWrapper>
          <SidebarSpace />
          {/* Admin Menu */}
          <SidebarGroup label="Admin">
            <SidebarDivider />
            <RequirePermission
              permission={devToolsAdministerPermission}
              errorPage={<></>}
            >
              {appVisualizerRendered}
              {devtoolsRendered}
            </RequirePermission>
          </SidebarGroup>
          {/* User Notifcations and Settings Area */}
          <SidebarDivider />
          <SidebarGroup label="Settings">
            <NotificationsSidebarItem />
            {userSettingsRendered}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
